import { supabase } from "./supabase";
import type { PostgrestError } from "@supabase/supabase-js";

// In-flight request coalescer. Concurrent callers with IDENTICAL (rpcName,params)
// share ONE network request. Different params never coalesce (different query).
type RpcResult<T> = { data: T | null; error: PostgrestError | null };
const pending = new Map<string, Promise<unknown>>();

function stableKey(rpcName: string, params: Record<string, unknown>): string {
  const sorted = Object.keys(params)
    .sort()
    .reduce(
      (o, k) => {
        o[k] = params[k];
        return o;
      },
      {} as Record<string, unknown>
    );
  return `${rpcName}|${JSON.stringify(sorted)}`;
}

export function coalesceRpc<T = unknown>(
  rpcName: string,
  params: Record<string, unknown>
): Promise<RpcResult<T>> {
  // Defensive: callers already guard `if (!supabase) return` upstream; never reached in-app.
  if (!supabase) return Promise.resolve({ data: null, error: null });
  const key = stableKey(rpcName, params);
  const existing = pending.get(key);
  if (existing) return existing as Promise<RpcResult<T>>;
  // .then((res) => res) forces the PostgrestFilterBuilder thenable into a real Promise to register.
  const p = supabase.rpc(rpcName, params).then((res) => res) as Promise<RpcResult<T>>;
  pending.set(key, p);
  p.finally(() => {
    if (pending.get(key) === p) pending.delete(key);
  });
  return p;
}
